import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Globe, PlusCircle, Type } from 'lucide-react';
import { api } from '../api/client';
import { useSelectedDomain } from '../store/selectedDomain';
import AuthLayout from '../components/AuthLayout';
import ErrorBanner from '../components/ErrorBanner';
import { toast } from '../lib/toast';

const DESCRIPTION_MAX = 2000;
// Mirrors the key the Dashboard checks to decide whether to bounce a
// zero-domain user back here. Setting it lets a new user skip onboarding.
const ONBOARDING_SKIPPED_KEY = 'rai_onboarding_skipped';

// Turns an Axios error into the most specific message we can show. Zod
// validation failures come back as { error: 'Validation failed', details:
// { fieldErrors: { description: ['...'] }, formErrors: [...] } } — the generic
// top-line `error` names no field, so we dig out the field-level messages
// (which already read like sentences) and join them.
function errorMessage(err, fallback) {
  const data = err.response?.data;
  const fieldErrors = data?.details?.fieldErrors;
  if (fieldErrors && typeof fieldErrors === 'object') {
    const labels = { domain: 'Domain', displayName: 'Display name', description: 'Description' };
    const messages = Object.entries(fieldErrors)
      .flatMap(([field, msgs]) =>
        (msgs || []).map((m) => `${labels[field] || field}: ${m}`),
      );
    if (messages.length) return messages.join(' ');
  }
  const formErrors = data?.details?.formErrors;
  if (Array.isArray(formErrors) && formErrors.length) return formErrors.join(' ');
  return data?.error || fallback;
}

export default function AddDomain() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setDomainId = useSelectedDomain((s) => s.setDomainId);
  const [form, setForm] = useState({ domain: '', displayName: '', description: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // The Dashboard auto-redirects to /domains/new whenever the user has zero
  // domains — so a "Back to dashboard" link would just loop the user right
  // back here. We query the domain count to decide whether to show the link
  // AND to swap the title between first-time vs subsequent adds.
  const domainsQuery = useQuery({
    queryKey: ['domains'],
    queryFn: () => api.get('/api/domains').then((r) => r.data.domains),
  });
  const hasExistingDomains = (domainsQuery.data?.length || 0) > 0;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/api/domains', form);
      // Seed the ['domains'] cache with the new domain so the Dashboard sees it
      // immediately. Otherwise it reads the stale (possibly empty) cache, decides
      // the user has zero domains, and bounces them right back here.
      queryClient.setQueryData(['domains'], (old) => [...(old || []), data.domain]);
      queryClient.invalidateQueries({ queryKey: ['domains'] });
      setDomainId(data.domain._id);
      // They've onboarded — drop any prior "skip" so a future zero-domain
      // state nudges them again.
      localStorage.removeItem(ONBOARDING_SKIPPED_KEY);
      toast.success(`Tracking ${data.domain.domain}`);
      navigate('/');
    } catch (err) {
      setError(errorMessage(err, 'Could not add domain'));
    } finally {
      setLoading(false);
    }
  };

  // First-run only: let the user into an (empty) dashboard without adding a
  // domain yet. The Dashboard reads ONBOARDING_SKIPPED_KEY and shows an empty
  // state instead of redirecting back here.
  const skipOnboarding = () => {
    localStorage.setItem(ONBOARDING_SKIPPED_KEY, '1');
    navigate('/');
  };

  return (
    <AuthLayout
      title={hasExistingDomains ? 'Add a domain' : 'Add your first domain'}
      subtitle={
        hasExistingDomains
          ? 'Track another site for AI referrals, crawler activity, and audits.'
          : "We'll start tracking AI referrals and crawlers as soon as you install the snippet."
      }
      footer={
        hasExistingDomains ? (
          <Link to="/" className="inline-flex items-center gap-1.5 text-brand-600 hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </Link>
        ) : (
          <button
            type="button"
            onClick={skipOnboarding}
            className="text-ink-400 hover:text-brand-600 hover:underline"
          >
            Skip for now &rarr;
          </button>
        )
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="label">Domain</label>
          <div className="relative">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
            <input
              className="input pl-10"
              placeholder="example.com"
              required
              value={form.domain}
              onChange={(e) => setForm({ ...form, domain: e.target.value })}
            />
          </div>
          <p className="text-xs text-ink-400 mt-1.5">
            Just the hostname — we&apos;ll strip protocols and trailing slashes.
          </p>
        </div>
        <div>
          <label className="label">Display name (optional)</label>
          <div className="relative">
            <Type className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
            <input
              className="input pl-10"
              value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              placeholder="Acme Marketing Site"
            />
          </div>
        </div>
        <div>
          <label className="label">Description (optional)</label>
          <textarea
            className="input min-h-[80px]"
            value={form.description}
            maxLength={DESCRIPTION_MAX}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="What does this site do? (used in /llms.txt)"
          />
          <p className="text-xs text-ink-400 mt-1.5 text-right">
            {form.description.length}/{DESCRIPTION_MAX}
          </p>
        </div>
        {error && <ErrorBanner body={error} onDismiss={() => setError('')} />}
        <button className="btn-primary w-full" disabled={loading}>
          {loading ? (
            'Adding…'
          ) : (
            <>
              <PlusCircle className="w-4 h-4" /> Add domain
            </>
          )}
        </button>
      </form>
    </AuthLayout>
  );
}
